/**
 * Signature the compiled-PDF hook keys its refetch on.
 *
 * Only the latest *successfully compiled* version can change what the PDF
 * proxy serves, so pending → running → failed churn on newer versions must
 * not move it (that caused the viewer to flash).
 *
 * `marked` is part of the signature because a recompile of the SAME version
 * (the admin free-plan preview, or dropping the credit line after an upgrade)
 * keeps the id and never writes `artifact_source_key`.
 *
 * `compiled_at` closes the remaining hole: `artifact_path` is stable per
 * version and overwritten in place, so two compiles of the same version with
 * the same `marked` value are otherwise indistinguishable. Without it the
 * viewer only refreshed by accident — while the row sat at `pending`, the
 * "latest successful version" briefly fell back to an older row. A missed
 * Realtime event or a very fast compile then left a stale PDF on screen.
 */
export interface PdfSignatureVersion {
  id: string;
  compile_status?: string | null;
  artifact_source_key?: string | null;
  marked?: boolean | null;
  compiled_at?: string | null;
}

export function buildPdfVersionSignature(versions: PdfSignatureVersion[]): string {
  const latest = versions.find((v) => v.compile_status === 'success');
  if (!latest) return '';
  return [
    latest.id,
    latest.artifact_source_key ?? '',
    latest.marked ? 'marked' : 'clean',
    latest.compiled_at ?? '',
  ].join(':');
}
